/* eslint-disable */
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToast } from 'primevue/usetoast'
import { getCampaignStats, getCampaigns, deleteCampaign, getCampaignReport } from '@/services/microlearningService'
import {fetchUserSubscription} from '@/services/subscriptionService'
export function useMicroLearningCampaign() {
  const { t } = useI18n()
  const toast = useToast()
  
  // State
  const loading = ref(false)
  const campaigns = ref([])
  const campaignStats = ref({
    waiting: 0,
    processing: 0,
    completed: 0,
    credit_mail: 0
  })
  const searchTerm = ref(null)
  const selectedStatus = ref(null)
  const showDeleteDialog = ref(false)
  const campaignToDelete = ref(null)
  const deleteConfirmationName = ref('')
  
  // Subscription and organization data
  const subscription = ref(null)
  const organization = ref(null)
  
  // Computed
  const filteredCampaigns = computed(() => {
    let filtered = campaigns.value
    
    // Filter by search term
    if (searchTerm.value) {
      const search = searchTerm.value.toLowerCase()
      filtered = filtered.filter(campaign => 
        campaign.name.toLowerCase().includes(search) ||
        campaign.description?.toLowerCase().includes(search) ||
        campaign.type.toLowerCase().includes(search)
      )
    }
    
    // Filter by status
    if (selectedStatus.value) {
      filtered = filtered.filter(campaign => 
        campaign.statusType === selectedStatus.value
      )
    }
    
    return filtered
  })
  
  // Methods
  const retrieveUserSubscription = async () => {
    try {
      const response = await fetchUserSubscription()
      if (response.data && response.remain_days > 0) {
        subscription.value = response.data
        return response.data
      }
    } catch (error) {
      console.error('Error retrieving user subscription:', error)
      toast.add({
        severity: 'error',
        summary: t('error.title'),
        detail: t('error.subscription_fetch'),
        life: 3000
      })
    }
    return null
  }
  
  const getOrganization = async () => {
    try {
      const response = await axios.get('/api/organization')
      if (response.data && response.data.success) {
        organization.value = response.data.data
        return response.data.data
      }
    } catch (error) {
      console.error('Error fetching organization:', error)
      toast.add({
        severity: 'error',
        summary: t('error.title'),
        detail: t('error.organization_fetch'),
        life: 3000
      })
    }
    return null
  }
  
  const loadCampaignStats = async () => {

    try {
      const response = await getCampaignStats()
      console.log(response)
      if (response) {
        campaignStats.value = {
          waiting: response.microlearning_waiting || 0,
          processing: response.microlearning_processing || 0,
          completed: response.microlearning_completed || 0,
          credit_mail: response?.credit_mail || 0
        }
      }
    } catch (error) {
      console.error('Error loading campaign stats:', error)
      toast.add({
        severity: 'error',
        summary: t('error.title'),
        detail: t('error.stats_fetch'),
        life: 3000
      })
    }
  }
  
  const loadCampaigns = async () => {
    try {
      loading.value = true
      const response = await getCampaigns()

      
      if (response) {
        const campaignsData = response || []
        
        // Process campaigns data
        campaigns.value = campaignsData.map(campaign => {
          const processedCampaign = {
            id: campaign.id,
            name: campaign.name || t('micro-campaign.unnamed_campaign'),
            description: campaign.description || '',
            type: campaign.campaign_type || t('micro-campaign.unknown_type'),
            status: getStatusLabel(campaign.status),
            statusType: campaign.status,
            creationDate: campaign.created_at,
            validationDate: campaign.validated_at,
            target: campaign?.service ? campaign?.service?.name : campaign?.group?.name,
            originalData: campaign
          }
          
          return processedCampaign
        })
        
        // Sort by creation date (newest first)
        campaigns.value.sort((a, b) => new Date(b.creationDate) - new Date(a.creationDate))
      }
    } catch (error) {
      console.error('Error loading campaigns:', error)
      toast.add({
        severity: 'error',
        summary: t('error.title'),
        detail: t('error.campaigns_fetch'),
        life: 3000
      })
    } finally {
      loading.value = false
    }
  }
  
  const getStatusLabel = (status) => {
    const statusMap = {
      'waiting': t('micro-campaign.waiting'),
      'processing': t('micro-campaign.processing'),
      'completed': t('micro-campaign.completed'),
      'canceled': t('micro-campaign.canceled'),
      'abandoned': t('micro-campaign.abandoned'),
      'closed': t('micro-campaign.closed'),
      'partial': t('micro-campaign.partial'),
      'failed': t('micro-campaign.failed'),
      'success': t('micro-campaign.processing'),
      'created': t('micro-campaign.created'),
      'In Progress': t('micro-campaign.in_progress')
    }
    
    return statusMap[status] || status
  }
  
  const getStatusSeverity = (status) => {
    const severityMap = {
      'completed': 'success',
      'success': 'warn',
      'processing': 'warn',
      'waiting': 'info',
      'created': 'info',
      'canceled': 'secondary',
      'abandoned': 'secondary',
      'closed': 'secondary',
      'partial': 'warning',
      'failed': 'danger',
      'In Progress': 'warn'

    }
    
    return severityMap[status] || 'secondary'
  }
  
  const formatDate = (dateString, showTime = false) => {
    if (!dateString) return t('common.not_available')
    
    try {
      const date = new Date(dateString)
      const options = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }

      if (showTime) {
        options.hour = '2-digit'
        options.minute = '2-digit'
      }

      return date.toLocaleDateString('fr-FR', options)
    } catch (error) {
      return dateString
    }
  }
  
  const refreshData = async () => {
    loading.value = true
    try {
      await Promise.all([
        loadCampaignStats(),
        loadCampaigns()
      ])
      
      toast.add({
        severity: 'success',
        summary: t('success.title'),
        detail: t('success.data_refreshed'),
        life: 3000
      })
    } catch (error) {
      console.error('Error refreshing data:', error)
      toast.add({
        severity: 'error',
        summary: t('error.title'),
        detail: t('error.refresh_failed'),
        life: 3000
      })
    } finally {
      loading.value = false
    }
  }
  
  const openNewCampaignModal = () => {
    // This would typically open a modal or navigate to a new campaign creation page
    toast.add({
      severity: 'info',
      summary: t('info.title'),
      detail: t('micro-campaign.new_campaign_feature_coming'),
      life: 3000
    })
  }
  
  const viewCampaign = (campaign) => {
    // This would typically navigate to a campaign detail view
    console.log(campaign)
    window.location.href = `/campaign-details/${campaign.id}`
    /*toast.add({
      severity: 'info',
      summary: t('info.title'),
      detail: t('micro-campaign.view_campaign_feature_coming'),
      life: 3000
    })*/
  }
  
  const downloadReport = async (campaign) => {
    try {
      loading.value = true
      
      if (campaign.statusType !== 'completed') {
        toast.add({
          severity: 'warn',
          summary: t('common.warn'),
          detail: t('micro-campaign.report_only_completed'),
          life: 3000
        })
        return
      }
      
      const response = getCampaignReport(campaign.id)
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `campagne_${campaign.name}_rapport.pdf`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      
      toast.add({
        severity: 'success',
        summary: t('common.success'),
        detail: t('micro-campaign.report_downloaded'),
        life: 3000
      })
    } catch (error) {
      console.error('Error downloading report:', error)
      
      if (error.response?.status === 403) {
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: t('micro-campaign.report_only_completed'),
          life: 3000
        })
      } else {
        toast.add({
          severity: 'error',
          summary: t('error.title'),
          detail: t('error.download_failed'),
          life: 3000
        })
      }
    } finally {
      loading.value = false
    }
  }
  
  const confirmDeleteCampaign = (campaign) => {
    campaignToDelete.value = campaign
    deleteConfirmationName.value = ''
    showDeleteDialog.value = true
  }
  
  const goDeleteCampaign = async () => {
    if (!campaignToDelete.value || deleteConfirmationName.value !== campaignToDelete.value.name) {
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('micro-campaign.name_mismatch'),
        life: 3000
      })
      return
    }
    
    try {
      loading.value = true
      
      const response = await deleteCampaign(campaignToDelete.value.id)
      
      if (response.data && response.data.success) {
        toast.add({
          severity: 'success',
          summary: t('common.success'),
          detail: t('micro-campaign.campaign_deleted'),
          life: 3000
        })
        
        // Refresh data
        await loadCampaigns()
        await loadCampaignStats()
      } else {
        throw new Error('Delete failed')
      }
    } catch (error) {
      console.error('Error deleting campaign:', error)
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('micro-campaign.campaign_deleted_failed'),

        life: 3000
      })
    } finally {
      loading.value = false
      cancelDelete()
    }
  }
  
  const cancelDelete = () => {
    showDeleteDialog.value = false
    campaignToDelete.value = null
    deleteConfirmationName.value = ''
  }
  
  // Initialize data on mount
  onMounted(async () => {
    await retrieveUserSubscription()
    //await getOrganization()
    loadCampaignStats()
    loadCampaigns()
  })
  
  return {
    // State
    loading,
    campaigns,
    campaignStats,
    searchTerm,
    selectedStatus,
    filteredCampaigns,
    showDeleteDialog,
    campaignToDelete,
    deleteConfirmationName,
    subscription,
    organization,
    
    // Methods
    loadCampaigns,
    loadCampaignStats,
    refreshData,
    formatDate,
    getStatusSeverity,
    openNewCampaignModal,
    viewCampaign,
    downloadReport,
    confirmDeleteCampaign,
    goDeleteCampaign,
    cancelDelete
  }
}